import React, { useEffect, useState } from 'react';
import NavigationBar from '../core/navigation_bar/navigation_bar';
import Footer from '../core/footer/footer';
import DashboardCalendar from './DashboardCalendar';
import { pageHeader, headers, formatDisplayDate, formatDisplayDateTime } from "../global_vars";
import { documentExpiryColor } from '../common/documentExpiry';
import RichTextDisplay from '../common/RichTextDisplay';
import RichTextEditor from '../common/RichTextEditor';
import axios from 'axios';
import { Modal, Button, Form } from 'react-bootstrap';
import GuardedModal from "../common/GuardedModal";
import Swal from 'sweetalert2';
import { FaTasks, FaBell, FaCalendarAlt, FaHistory, FaFileAlt, FaThLarge, FaBullhorn } from 'react-icons/fa';

const API_BASE = "https://ultima.icsgr.com";

function TaskLine({ t }) {
  return (
    <li>
      <a href={`/data_management/project_task/${t.projtask_id}`}>{t.title}</a>
      {(t.project_title || t.project_filecode) ? (
        <span style={{ marginLeft: 8, color: '#666' }}>
          file:{" "}
          {t.project_id ? (
            <a href={`/data_management/project/${t.project_id}`}>
              {t.project_title || t.project_filecode}
              {t.project_title && t.project_filecode ? ` (${t.project_filecode})` : ""}
            </a>
          ) : (
            t.project_title || t.project_filecode
          )}
        </span>
      ) : null}
      {t.deadline ? (<span style={{ marginLeft: 8, color: '#888' }}>deadline: {formatDisplayDate(t.deadline)}</span>) : null}
    </li>
  );
}

function TaskGroup({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <ul className="mb-0" style={{ listStyleType: 'circle', marginLeft: 10 }}>
        {items.map((t) => (
          <TaskLine key={t.projtask_id} t={t} />
        ))}
      </ul>
    </div>
  );
}

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showAllActions, setShowAllActions] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [loadError, setLoadError] = useState(null);
  const [showAnnModal, setShowAnnModal] = useState(false);
  const [annTitle, setAnnTitle] = useState('');
  const [annBody, setAnnBody] = useState('');
  const [savingAnn, setSavingAnn] = useState(false);

  const authHeaders = () => ({
    ...headers,
    Authorization: 'Token ' + localStorage.getItem('userToken'),
  });

  const load = async () => {
    setLoadError(null);
    try {
      const res = await axios.get(API_BASE + '/api/dashboard/my/', { headers: authHeaders() });
      if (res?.data?.success === false) {
        setLoadError(res?.data?.error || 'Failed to load dashboard');
        setData({});
      } else {
        setData(res?.data?.data || {});
      }
    } catch (e) {
      setLoadError(e?.response?.data?.error || e?.message || 'Failed to load dashboard');
      setData({});
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const canManage = !!data?.can_manage_announcements;

  const createAnnouncement = async () => {
    if (!annTitle.trim()) {
      Swal.fire({ icon: 'warning', title: 'Title required' });
      return;
    }
    setSavingAnn(true);
    try {
      await axios.post(API_BASE + '/api/dashboard/announcements/', {
        title: annTitle.trim(),
        body: annBody,
        active: true,
      }, { headers: authHeaders() });
      setShowAnnModal(false);
      setAnnTitle('');
      setAnnBody('');
      await load();
      Swal.fire({ icon: 'success', title: 'Announcement posted', timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e?.response?.data?.error || 'Failed to create announcement',
      });
    } finally {
      setSavingAnn(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Delete announcement?',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!confirm.isConfirmed) return;
    try {
      await axios.delete(API_BASE + `/api/dashboard/announcements/${id}/`, { headers: authHeaders() });
      await load();
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e?.response?.data?.error || 'Failed to delete',
      });
    }
  };

  return (
    <>
      <NavigationBar />
      <div className="mainContainer">
        <div className="contentContainer">
          <div className="contentBody">
            {pageHeader("dashboard", "Dashboard")}
            {!loaded ? (
              <div>Loading...</div>
            ) : (
              <div className="container">
                <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === 'cards' ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setViewMode('cards')}
                  >
                    <FaThLarge style={{ marginRight: 6 }} />
                    Overview
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === 'calendar' ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setViewMode('calendar')}
                  >
                    <FaCalendarAlt style={{ marginRight: 6 }} />
                    Calendar
                  </button>
                </div>
                {viewMode === 'calendar' ? (
                  <DashboardCalendar />
                ) : null}
                {loadError ? (
                  <div className="alert alert-danger" role="alert">{loadError}</div>
                ) : null}
                {viewMode === 'cards' ? (
              <>
                <div className="row">
                  <div className="col-md-6">
                    <div className="card" style={{ marginBottom: 14 }}>
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span><FaBullhorn style={{ color: '#93ab3c', marginRight: 8 }} />Announcements</span>
                        {canManage ? (
                          <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setShowAnnModal(true)}>
                            Add
                          </button>
                        ) : null}
                      </div>
                      <div className="card-body">
                        {(data?.announcements || []).length === 0 ? (<div>No announcements</div>) : (
                          <ul className="mb-0" style={{ listStyleType: 'none', paddingLeft: 0 }}>
                            {(data?.announcements || []).map(a => (
                              <li key={a.announcement_id} style={{ marginBottom: 14, borderBottom: '1px solid #eee', paddingBottom: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                  <div style={{ fontWeight: 700 }}>{a.title}</div>
                                  {canManage ? (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => deleteAnnouncement(a.announcement_id)}
                                    >
                                      Remove
                                    </button>
                                  ) : null}
                                </div>
                                <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>
                                  {formatDisplayDateTime(a.created_at)}
                                  {a.created_by_username ? ` · ${a.created_by_username}` : ''}
                                </div>
                                <RichTextDisplay html={a.body} emptyLabel="" />
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card" style={{ marginBottom: 14 }}>
                      <div className="card-header"><FaTasks style={{ color: '#93ab3c', marginRight: 8 }} />My Tasks </div>
                      <div className="card-body">
                        {(() => {
                          const mine = data?.tasks_assigned_to_me || data?.tasks || [];
                          const others = data?.tasks_assigned_to_others || [];
                          if (!mine.length && !others.length) return <div>No items</div>;
                          return (
                            <>
                              <TaskGroup title="Assigned to me" items={mine} />
                              <TaskGroup title="Assigned to others" items={others} />
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="card" style={{ marginBottom: 14 }}>
                      <div className="card-header"><FaBell style={{ color: '#93ab3c', marginRight: 8 }} />My Notifications (unread)</div>
                      <div className="card-body">
                        <ul className="mb-0" style={{ listStyleType: 'circle', marginLeft: 10 }}>
                          {(data?.notifications || []).map(n => (
                            <li key={n.id}>
                              <a href={`/notifications`}>{n.type}: {n.message}</a>
                            </li>
                          ))}
                          {(data?.notifications || []).length === 0 ? (<div>No items</div>) : null}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card" style={{ marginBottom: 14 }}>
                      <div className="card-header"><FaCalendarAlt style={{ color: '#93ab3c', marginRight: 8 }} />Deadlines (next 14 days)</div>
                      <div className="card-body">
                        {(() => {
                          const list = Array.isArray(data?.deadlines) ? data.deadlines : [];
                          const proj = list.filter(d => d.type === 'project');
                          const tasks = list.filter(d => d.type === 'task');
                          const tasksToMe = tasks.filter(d => d.relation === 'to_me');
                          const tasksToOthers = tasks.filter(d => d.relation === 'to_others');
                          const tasksOnFile = tasks.filter(d => d.relation !== 'to_me' && d.relation !== 'to_others');
                          const tax = list.filter(d => d.type === 'taxation_project');
                          const Section = ({ title, items }) => (
                            items.length ? (
                              <div style={{ marginBottom: 10 }}>
                                <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}: {items.length}</div>
                                <ul className="mb-0" style={{ listStyleType: 'circle', marginLeft: 10 }}>
                                  {items.map(d => (
                                    <li key={`${d.type}_${d.id}`}>
                                      {d.type === 'project' && (
                                        <a href={`/data_management/project/${d.id}`}>{d.title}</a>
                                      )}
                                      {d.type === 'task' && (
                                        <>
                                          <a href={`/data_management/project_task/${d.id}`}>{d.title}</a>
                                          {d.project_id ? (
                                            <span style={{ marginLeft: 6, color: '#666' }}>
                                              (
                                              <a href={`/data_management/project/${d.project_id}`}>
                                                {d.project_title || d.project_id}
                                              </a>
                                              )
                                            </span>
                                          ) : null}
                                        </>
                                      )}
                                      {d.type === 'taxation_project' && (
                                        <a href={`/data_management/taxation_project/${d.id}`}>{d.title}</a>
                                      )}
                                      <span style={{
                                        marginLeft: 8,
                                        color: d.deadline && new Date(d.deadline) < new Date(new Date().toDateString()) ? '#c0392b' : '#e67e22',
                                        fontWeight: 600,
                                      }}>
                                        {formatDisplayDate(d.deadline)}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null
                          );
                          return (
                            <>
                              <Section title="Projects" items={proj} />
                              <Section title="Assigned to me" items={tasksToMe} />
                              <Section title="Assigned to others" items={tasksToOthers} />
                              <Section title="Project tasks" items={tasksOnFile} />
                              <Section title="Taxation Projects" items={tax} />
                              {(!proj.length && !tasksToMe.length && !tasksToOthers.length && !tasksOnFile.length && !tax.length) ? (<div>No items</div>) : null}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="card" style={{ marginBottom: 14 }}>
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span><FaHistory style={{ color: '#93ab3c', marginRight: 8 }} />My latest actions</span>
                        {(data?.actions || []).length > 5 ? (
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowAllActions(v => !v)}>
                            {showAllActions ? 'Show less' : 'Show more'}
                          </button>
                        ) : null}
                      </div>
                      <div className="card-body">
                        <ul className="mb-0" style={{ listStyleType: 'circle', marginLeft: 10 }}>
                          {((data?.actions || []).slice(0, showAllActions ? 20 : 5)).map(a => (
                            <li key={a.id}>{a.label || a.action} — {formatDisplayDateTime(a.occurred_at)}</li>
                          ))}
                          {(data?.actions || []).length === 0 ? (<div>No items</div>) : null}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card" style={{ marginBottom: 14 }}>
                      <div className="card-header"><FaFileAlt style={{ color: '#93ab3c', marginRight: 8 }} />Expiring documents (next 30 days)</div>
                      <div className="card-body">
                        <ul className="mb-0" style={{ listStyleType: 'circle', marginLeft: 10 }}>
                          {(data?.expiring_documents || []).map(doc => (
                            <li key={doc.document_id}>
                              <a href={`/data_management/document/${doc.document_id}`}>{doc.title}</a>
                              <span style={{ marginLeft: 8, color: documentExpiryColor(doc.validuntil) || '#888', fontWeight: doc.urgency ? 700 : 400 }}>
                                {formatDisplayDate(doc.validuntil)}
                                {doc.days_until != null ? ` (${doc.days_until}d)` : ''}
                              </span>
                              {doc.project__project_id ? (<a style={{ marginLeft: 8 }} href={`/data_management/project/${doc.project__project_id}`}>project {doc.project__project_id}</a>) : null}
                            </li>
                          ))}
                          {(data?.expiring_documents || []).length === 0 ? (<div>No items</div>) : null}
                        </ul>
                      </div>
                    </div>
                    <div className="card" style={{ marginBottom: 14 }}>
                      <div className="card-header"><FaFileAlt style={{ color: '#93ab3c', marginRight: 8 }} />Expiring passports (next 30 days)</div>
                      <div className="card-body">
                        <ul className="mb-0" style={{ listStyleType: 'circle', marginLeft: 10 }}>
                          {(data?.expiring_passports || []).map(p => (
                            <li key={p.client_id}>
                              <a href={`/data_management/client/${p.client_id}`}>{p.client_name}</a>
                              {p.passportnumber ? <span style={{ marginLeft: 6, color: '#666' }}>({p.passportnumber})</span> : null}
                              <span style={{ marginLeft: 8, color: documentExpiryColor(p.passportexpiredate) || '#888', fontWeight: p.urgency ? 700 : 400 }}>
                                {formatDisplayDate(p.passportexpiredate)}
                                {p.days_until != null ? ` (${p.days_until}d)` : ''}
                              </span>
                              {p.passportissuedate ? (
                                <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>
                                  issued {formatDisplayDate(p.passportissuedate)}
                                </span>
                              ) : null}
                            </li>
                          ))}
                          {(data?.expiring_passports || []).length === 0 ? (<div>No items</div>) : null}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <GuardedModal show={showAnnModal} onHide={() => setShowAnnModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>New announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} maxLength={120} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Body</Form.Label>
            <RichTextEditor value={annBody} onChange={setAnnBody} placeholder="Announcement details…" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAnnModal(false)}>Cancel</Button>
          <Button variant="success" disabled={savingAnn} onClick={createAnnouncement}>
            {savingAnn ? 'Saving…' : 'Post'}
          </Button>
        </Modal.Footer>
      </GuardedModal>

      <Footer />
    </>
  );
};

export default Dashboard;
